import { useEffect, useState } from "react";

/**
 * Subscribes to a CSS media query and re-renders when it starts or stops
 * matching. Used for viewport, pointer and reduced-motion checks that need to
 * gate JavaScript behaviour rather than just styling, where a Tailwind
 * breakpoint or a `motion-safe:` variant would not be enough.
 *
 * The initialiser reads `matchMedia` synchronously so the first paint already
 * reflects the correct value instead of flashing the fallback.
 */
export function useMatchMedia(query: string): boolean {
    const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

    useEffect(() => {
        const mql = window.matchMedia(query);
        const handler = (event: MediaQueryListEvent) => setMatches(event.matches);
        // Re-read on subscribe: the query may have changed between the initial
        // render and this effect running.
        setMatches(mql.matches);
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
    }, [query]);

    return matches;
}
